import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModelPipeline3D from './ModelPipeline3D';
import { AUTOMATION_STEPS } from '../data/service';
import { DECKS } from '../models/decks';
import { getStageSamples } from '../models/samples';
import './ModelDetailViewer.css';

const MODELS = [{ model: 'overview', short: '전체 구조' }, ...AUTOMATION_STEPS];

export default function ModelDetailViewer({ initialModel = 'fabric', initialStage = 0, returnFocusTo, onClose }) {
  const firstModel = MODELS.some((item) => item.model === initialModel) ? initialModel : 'fabric';
  const [model, setModel] = useState(firstModel);
  const [stage, setStage] = useState(() => Number.isFinite(initialStage) ? Math.max(0, Math.min(DECKS[firstModel].steps.length - 1, Math.trunc(initialStage))) : 0);
  const [sample, setSample] = useState(null);
  const [zoom, setZoom] = useState(1);
  const titleId = useId();
  const helpId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const rendererRef = useRef(null);
  const infoRef = useRef(null);
  const deck = DECKS[model];
  const step = deck.steps[stage];
  const samples = getStageSamples(model, stage);
  const modelLabel = MODELS.find((item) => item.model === model).short;

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = returnFocusTo || document.activeElement;
    const originalOverflow = document.body.style.overflow;
    const originalPadding = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0 && document.documentElement.clientWidth > 0) {
      const padding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${padding + scrollbar}px`;
    }
    document.body.style.overflow = 'hidden';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      if (typeof dialog.close === 'function') dialog.close();
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPadding;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [returnFocusTo]);

  const onViewChange = useCallback((view) => setZoom(view.zoom), []);
  const onPick = useCallback((picked) => setSample(picked), []);
  const clearSample = () => {
    setSample(null);
    rendererRef.current?.selectSample(-1);
    if (infoRef.current) infoRef.current.scrollTop = 0;
  };
  const changeModel = (next) => {
    if (next === model) return;
    setModel(next);
    setStage(0);
    setZoom(1);
    clearSample();
  };
  const changeStage = (next) => {
    setStage(Math.max(0, Math.min(deck.steps.length - 1, next)));
    clearSample();
  };
  const resetView = () => {
    rendererRef.current?.resetView();
    setSample(null);
  };
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    // Native modal dialogs contain focus; keep the same behavior in fallback environments.
    const focusable = [...dialogRef.current.querySelectorAll('button:not(:disabled), select:not(:disabled), a[href], summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden]') && (!element.closest('details:not([open])') || element.tagName === 'SUMMARY'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  return createPortal(
    <dialog ref={dialogRef} className="mf-model-viewer" aria-modal="true" aria-labelledby={titleId} aria-describedby={helpId} onCancel={(event) => { event.preventDefault(); onClose(); }} onKeyDown={onKeyDown}>
      <header className="mf-viewer-header">
        <div><span className="mf-viewer-eyebrow">MARKETFIT · MODEL EXPLORER</span><h2 id={titleId}>3D 모델 자세히 보기</h2></div>
        <button ref={closeRef} type="button" className="mf-viewer-close" onClick={onClose} aria-label="3D 자세히 보기 닫기"><span>닫기</span><svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></button>
      </header>
      <div className="mf-viewer-models" role="group" aria-label="자세히 볼 모델">
        {MODELS.map((item) => <button key={item.model} type="button" aria-pressed={model === item.model} onClick={() => changeModel(item.model)}>{item.short}</button>)}
      </div>
      <div className="mf-viewer-body">
        <section className="mf-viewer-visual" aria-label={`${modelLabel} 확대 모델`}>
          <div className="mf-viewer-scene-heading"><span>{modelLabel} <i>/</i> {step.code}</span><p id={helpId}>드래그로 회전 · 휠 또는 버튼으로 확대 · 구성요소 클릭</p></div>
          <div className="mf-viewer-canvas">
            <ModelPipeline3D key={model} ref={rendererRef} model={model} stage={stage} interactionMode="viewer" cameraScale={1.3} onViewChange={onViewChange} onPick={onPick} />
          </div>
          <div className="mf-viewer-toolbar">
            <div className="mf-viewer-zoom" role="group" aria-label="모델 확대 축소">
              <button type="button" onClick={() => rendererRef.current?.zoomBy(1 / 1.2)} aria-label="모델 축소">−</button>
              <output aria-label="모델 확대 비율">{Math.round(zoom * 100)}%</output>
              <button type="button" onClick={() => rendererRef.current?.zoomBy(1.2)} aria-label="모델 확대">+</button>
            </div>
            <div className="mf-viewer-rotation" role="group" aria-label="모델 회전">
              <button type="button" onClick={() => rendererRef.current?.rotateBy(-Math.PI / 8)} aria-label="모델 왼쪽 회전">←</button>
              <button type="button" onClick={() => rendererRef.current?.rotateBy(Math.PI / 8)} aria-label="모델 오른쪽 회전">→</button>
              <button type="button" onClick={() => rendererRef.current?.rotateBy(0, -Math.PI / 12)} aria-label="모델 위로 회전">↑</button>
              <button type="button" onClick={() => rendererRef.current?.rotateBy(0, Math.PI / 12)} aria-label="모델 아래로 회전">↓</button>
            </div>
            <button type="button" className="mf-viewer-reset" onClick={resetView}>시점 초기화 <span aria-hidden="true">↺</span></button>
          </div>
        </section>
        <aside className="mf-viewer-info" ref={infoRef} aria-label="모델 상세 설명">
          <div className="mf-viewer-stage-head"><span>단계별 구조</span><span>{String(stage + 1).padStart(2, '0')} / {String(deck.steps.length).padStart(2, '0')}</span></div>
          <label htmlFor={`${titleId}-stage`} className="mf-viewer-label">세부 단계</label>
          <select id={`${titleId}-stage`} value={stage} onChange={(event) => changeStage(Number(event.target.value))}>
            {deck.steps.map((item, index) => <option key={item.code} value={index}>{index + 1}. {item.title}</option>)}
          </select>
          <div className="mf-viewer-stage-buttons"><button type="button" onClick={() => changeStage(stage - 1)} disabled={stage === 0}>← 이전 단계</button><button type="button" onClick={() => changeStage(stage + 1)} disabled={stage === deck.steps.length - 1}>다음 단계 →</button></div>
          <h3>{step.title}</h3>
          <p className="mf-viewer-shape">{step.shape}</p>
          {step.bullets && <ul>{step.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
          <div className="mf-viewer-samples">
            <h4>구성요소 살펴보기</h4>
            <p>모델의 입자를 클릭하거나 목록에서 선택해 연결된 데이터를 확인하세요.</p>
            <label htmlFor={`${titleId}-sample`} className="mf-viewer-label">구성요소 선택</label>
            <select id={`${titleId}-sample`} value={sample ? sample.group : ''} onChange={(event) => {
              const value = event.target.value;
              if (value === '') { setSample(null); rendererRef.current?.selectSample(-1); return; }
              const index = Number(value);
              setSample(samples[index]);
              rendererRef.current?.selectSample(index);
            }}><option value="">구성요소를 선택하세요</option>{samples.map((item, index) => <option key={index} value={index}>{item.name} · {item.kind}</option>)}</select>
            {sample && <div className="mf-viewer-selection"><strong>{sample.name}</strong><span>{sample.kind}</span><details key={`${model}-${stage}-${sample.group}`} open><summary>샘플 데이터 <span>SAMPLE JSON</span></summary><pre tabIndex={0} aria-label="선택한 구성요소 데이터">{sample.json}</pre></details></div>}
          </div>
          <p className="mf-viewer-footnote">서비스 구조를 설명하는 모의 데이터입니다.</p>
        </aside>
      </div>
    </dialog>, document.body
  );
}
